/*
 * Cross-tab logout via a single shared BroadcastChannel.
 *
 * In-memory tokens are per-tab, so logging out in one tab leaves other tabs holding
 * their own still-valid token. We broadcast a logout message; other tabs navigate
 * straight to the Okta end-session URL.
 *
 * Two safety rules, learned the hard way from the first attempt:
 *  1. Receivers NAVIGATE to the logout URL — never reload() into a SecureRoute, which
 *     would hit /authorize and could silently re-auth against a lingering SSO session.
 *  2. One shared channel instance is reused for both posting and subscribing. A
 *     BroadcastChannel never receives its own posts, so the tab that logs out does not
 *     trigger itself (the original bug used two instances, so it self-delivered). Even
 *     if it did, rule 1 makes it harmless — it would navigate to the same URL.
 */
const CHANNEL_NAME = 'aoc-auth';
const LOGOUT_TYPE = 'logout';

let channel;
const getChannel = () => {
  if (channel === undefined) {
    channel = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel(CHANNEL_NAME) : null;
  }
  return channel;
};

// Tell other tabs to log out, passing the Okta end-session URL they should navigate to.
export const signalLogout = (logoutUrl) => {
  getChannel()?.postMessage({ type: LOGOUT_TYPE, url: logoutUrl });
};

// Subscribe to logout messages from other tabs. Returns an unsubscribe function.
export const subscribeToLogout = (handler) => {
  const ch = getChannel();
  if (!ch) return () => {};
  const listener = (event) => {
    if (event.data?.type === LOGOUT_TYPE) handler(event.data.url);
  };
  ch.addEventListener('message', listener);
  return () => ch.removeEventListener('message', listener);
};
